import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { ApiError, api } from '../api/client.js'
import type { ObjectDetail } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { Field, Modal, Notice } from '../components/ui.js'
import { formatDateTime } from '../lib/format.js'

interface CustomerUser {
  id: string
  name: string
  email: string
  active: boolean
  siteIds: string[] | null
  lastSeenAt: string | null
  hasPassword: boolean
}

export interface Invite {
  link: string
  mailed: boolean
  expiresInDays: number
}

/**
 * Kundenzugänge on a customer: who from the owner's side may look into the
 * portal, and which Liegenschaften they see. The customer sets the password
 * through the link; the office never knows it.
 */
export function CustomerAccess({ node }: { node: ObjectDetail }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [editing, setEditing] = useState<CustomerUser | 'new' | null>(null)
  const [invite, setInvite] = useState<{ name: string; data: Invite } | null>(null)
  const sites = node.children.filter((c) => c.kind === 'site')
  const siteName = new Map(sites.map((s) => [s.id, s.name]))

  const { data } = useQuery({ queryKey: ['customer-users', node.id], queryFn: () => api<CustomerUser[]>(`/api/customers/${node.id}/users`) })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['customer-users', node.id] })

  const newLink = useMutation({
    mutationFn: (user: CustomerUser) => api<Invite>(`/api/customer-users/${user.id}/invite`, { method: 'POST' }),
    onSuccess: (result, user) => setInvite({ name: user.name, data: result }),
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Link konnte nicht erzeugt werden.', 'error'),
  })
  const toggle = useMutation({
    mutationFn: (user: CustomerUser) => api(`/api/customer-users/${user.id}`, { method: 'PATCH', body: { active: !user.active } }),
    onSuccess: async (_r, user) => {
      toast(user.active ? `Zugang von ${user.name} gesperrt.` : `Zugang von ${user.name} wieder aktiv.`)
      await refresh()
    },
  })

  return (
    <section className="card" style={{ marginBottom: 18 }}>
      <div className="card-head">
        <div>
          <h2>
            <Icon name="key" size={16} /> Kundenzugänge
          </h2>
          <div className="muted small">
            Im Kundenportal sieht der Kunde Erledigtes, Geplantes und Nachweise seiner Liegenschaften und meldet Anliegen. Namen der
            Mitarbeitenden sieht er nicht.
          </div>
        </div>
        <button type="button" className="btn" onClick={() => setEditing('new')}>
          <Icon name="plus" /> Zugang anlegen
        </button>
      </div>
      {data && data.length > 0 && (
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Sieht</th>
                <th className="nowrap">Status</th>
                <th className="nowrap">Zuletzt da</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((user) => (
                <tr key={user.id} style={user.active ? undefined : { opacity: 0.55 }}>
                  <td>
                    <span className="strong">{user.name}</span>
                    <div className="muted small">{user.email}</div>
                  </td>
                  <td className="small">
                    {user.siteIds && user.siteIds.length > 0 ? user.siteIds.map((id) => siteName.get(id) ?? 'unbekannt').join(', ') : 'alle Liegenschaften'}
                  </td>
                  <td className="nowrap">
                    {!user.active ? (
                      <span className="badge skipped">gesperrt</span>
                    ) : user.hasPassword ? (
                      <span className="badge done">eingerichtet</span>
                    ) : (
                      <span className="badge warn">Einladung offen</span>
                    )}
                  </td>
                  <td className="nowrap small">{user.lastSeenAt ? formatDateTime(user.lastSeenAt) : <span className="muted">noch nie</span>}</td>
                  <td className="nowrap" style={{ textAlign: 'right' }}>
                    <button type="button" className="btn ghost small" onClick={() => setEditing(user)}>
                      Bearbeiten
                    </button>
                    {user.active && (
                      <button type="button" className="btn ghost small" onClick={() => newLink.mutate(user)}>
                        {user.hasPassword ? 'Passwort zurücksetzen' : 'Neuer Link'}
                      </button>
                    )}
                    <button type="button" className="btn ghost small" onClick={() => toggle.mutate(user)}>
                      {user.active ? 'Sperren' : 'Entsperren'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <AccessModal
          customerId={node.id}
          user={editing === 'new' ? null : editing}
          sites={sites}
          onClose={() => setEditing(null)}
          onSaved={async (result) => {
            setEditing(null)
            await refresh()
            if (result) setInvite(result)
          }}
        />
      )}

      <InviteModal invite={invite} who="der Kunde" onClose={() => setInvite(null)} />
    </section>
  )
}

/** The link that lets someone set their own password. Shown once, copyable. */
export function InviteModal({
  invite,
  who,
  onClose,
}: {
  invite: { name: string; data: Invite } | null
  who: string
  onClose: () => void
}) {
  const toast = useToast()
  return (
    <Modal open={invite !== null} title={`Einladung für ${invite?.name ?? ''}`} onClose={onClose}>
      {invite && (
        <>
          <Notice kind={invite.data.mailed ? 'success' : 'info'}>
            {invite.data.mailed
              ? 'Die Einladung ist per E-Mail unterwegs.'
              : 'Es ist kein E-Mail-Versand eingerichtet. Bitte den Link selbst weitergeben.'}{' '}
            Der Link ist {invite.data.expiresInDays} Tage gültig; damit legt {who} das Passwort selbst fest.
          </Notice>
          <Field label="Link">
            <input readOnly value={invite.data.link} onFocus={(e) => e.currentTarget.select()} />
          </Field>
          <button
            type="button"
            className="btn secondary"
            onClick={() => {
              void navigator.clipboard?.writeText(invite.data.link)
              toast('Link kopiert.')
            }}
          >
            Link kopieren
          </button>
        </>
      )}
    </Modal>
  )
}

function AccessModal({
  customerId,
  user,
  sites,
  onClose,
  onSaved,
}: {
  customerId: string
  user: CustomerUser | null
  sites: { id: string; name: string }[]
  onClose: () => void
  onSaved: (invite: { name: string; data: Invite } | null) => void
}) {
  const [siteIds, setSiteIds] = useState<string[]>(user?.siteIds ?? [])
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (body: { name: string; email?: string }) =>
      user
        ? api(`/api/customer-users/${user.id}`, { method: 'PATCH', body: { name: body.name, siteIds } }).then(() => null)
        : api<Invite & { id: string }>(`/api/customers/${customerId}/users`, { method: 'POST', body: { ...body, siteIds } }),
    onSuccess: (result, body) => onSaved(result ? { name: body.name, data: result } : null),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })
  return (
    <Modal
      open
      title={user ? `Zugang ${user.name}` : 'Kundenzugang anlegen'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" form="access-form" className="btn" disabled={save.isPending}>
            {user ? 'Speichern' : 'Anlegen und einladen'}
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <form
        id="access-form"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault()
          const form = new FormData(event.currentTarget)
          save.mutate({ name: String(form.get('name')), email: user ? undefined : String(form.get('email')) })
        }}
      >
        <Field label="Name" hint="z. B. Frau Schulte, Objektbetreuung LEG, oder die Sozialarbeiterin vor Ort.">
          <input name="name" required defaultValue={user?.name} autoFocus />
        </Field>
        {!user && (
          <Field label="E-Mail-Adresse" hint="An diese Adresse geht der Link, mit dem der Kunde sein Passwort festlegt.">
            <input name="email" type="email" required />
          </Field>
        )}
        <div className="field">
          <span>Liegenschaften</span>
          <div className="hint" style={{ marginTop: 0, marginBottom: 6 }}>
            Ohne Auswahl sieht der Zugang alle Liegenschaften dieses Kunden, auch künftige.
          </div>
          <div className="check-list">
            {sites.map((site) => (
              <label key={site.id}>
                <input
                  type="checkbox"
                  checked={siteIds.includes(site.id)}
                  onChange={(e) => setSiteIds(e.target.checked ? [...siteIds, site.id] : siteIds.filter((id) => id !== site.id))}
                />
                {site.name}
              </label>
            ))}
            {sites.length === 0 && <span className="muted small">Dieser Kunde hat noch keine Liegenschaften.</span>}
          </div>
        </div>
      </form>
    </Modal>
  )
}
